import { observer } from "mobx-react-lite";
import { BoxProps, useToast } from "@chakra-ui/react";

import { translate, useSelecting } from "@noli/business";
import { range } from "@noli/core";
import { toast } from "@/utils/misc";
import { Actions } from "@/utils/api";
import { Editor_Words } from "@/utils/lang/editor";
import { IGetTxt2ImgMetaData, Txt2ImgMetaData } from "@/types/meta";
import { getTxt2ImgMetaData } from "@/modules/editor/utils/meta";
import { useMemo } from "react";
import { GenerateBizType } from "@/types/creator";
import { GeneratePriceProps } from "@/modules/editor/utils/generateCost";
import BizGenerateButton from "@/components/bussiness/BizGenerateButton";

export type Txt2ImgSubmitButtonProps = Omit<IGetTxt2ImgMetaData, "displayNode"> & {
  t: ReturnType<typeof useToast>;
  canIgnoreText?: boolean;
  defaultBusyAlias: string;
  generateCount?: number;
  generateMetaFn?: (props: IGetTxt2ImgMetaData) => Txt2ImgMetaData;
  extraAdvanceMetaData?: Partial<Txt2ImgMetaData>;
  size: { w: number; h: number };
  calcPriceProps?: Omit<GeneratePriceProps, "size">;
  highResInfo?: IGetTxt2ImgMetaData["highResInfo"];
} & BoxProps;
export const Txt2ImgSubmitButton = observer<Txt2ImgSubmitButtonProps>(
  ({
    t,
    lang,
    text,
    circular,
    modelType,
    customEmbeddings,
    originalNegative,
    extraPromptInfo,
    canIgnoreText,
    defaultBusyAlias,
    generateCount = 1,
    txt2ImgCustomSize,
    generateMetaFn,
    extraAdvanceMetaData,
    size,
    calcPriceProps,
    highResInfo,
    ...others
  }) => {
    const displayNode = useSelecting("raw").displayNode;
    const useDisplayNodeInfo = generateCount === 1 && displayNode?.type === "rectangle";
    const busyAlias = useDisplayNodeInfo ? displayNode.alias : defaultBusyAlias;
    const busyAliases = range(0, generateCount || 1).map((i) => `${busyAlias}-${i}`);

    async function onSubmit(): Promise<void> {
      // sanity check
      if (!text && !canIgnoreText) {
        toast(t, "warning", translate(Editor_Words["no-text-input-message"], lang));
        return;
      }

      const _generateMetaFn = generateMetaFn ?? getTxt2ImgMetaData;
      const metaData = _generateMetaFn({
        lang,
        text,
        circular,
        modelType,
        displayNode,
        originalNegative,
        extraPromptInfo,
        txt2ImgCustomSize,
        customEmbeddings,
        highResInfo,
      });

      if (extraAdvanceMetaData) {
        Object.assign(metaData, extraAdvanceMetaData);
      }

      await Actions.importMeta({
        t,
        lang,
        type: "txt2img",
        meta: {
          type: "txt2img",
          data: metaData,
        },
        setupInfo: { checkBusy: false, busyAliases: busyAliases },
        aliasPrefix: "txt2img",
        opt: {
          freezePrompt: false,
          displayNode: useDisplayNodeInfo ? displayNode : undefined,
          generateCount,
        },
        bizType: extraPromptInfo?.cat ? GenerateBizType.Presets : GenerateBizType.Txt2Img,
      });
    }

    const btnText = useMemo(() => {
      if (!generateCount || generateCount === 1) {
        const text = translate(
          displayNode?.type === "rectangle"
            ? Editor_Words["txt2img-sd-submit-and-fill"]
            : Editor_Words["txt2img-sd-submit-and-add"],
          lang,
        );
        return text;
      } else {
        return translate(Editor_Words["txt2img-sd-submit-and-add-template"], lang).replace(
          "$1",
          generateCount.toString(),
        );
      }
    }, [generateCount, generateMetaFn, displayNode?.type]);

    return (
      <BizGenerateButton
        mt="16px"
        size={size}
        calcPriceProps={calcPriceProps}
        generateCount={generateCount}
        {...others}
        onSubmit={onSubmit}
        checkBusyConfig={{
          checkVip: true,
          busyAlias: busyAliases,
        }}
        sticky
      >
        {btnText}
      </BizGenerateButton>
    );
  },
);
